/**
 * Org module visibility (Admin → Modules).
 *
 * HR always sees every module. Everyone else only sees modules that
 * are switched on. Loaded once per session from GET /org/modules and
 * refreshed when HR flips a switch.
 *
 * Fail-open: if the fetch fails we treat every module as on, so a
 * transient API error never makes the app look empty.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { DEFAULT_ORG_MODULES, type OrgModuleKey, type OrgModuleSettings } from '@medlive-hris/shared-types'

export const useOrgModulesStore = defineStore('orgModules', () => {
  const modules = ref<OrgModuleSettings>({ ...DEFAULT_ORG_MODULES })
  const loaded = ref(false)
  let inflight: Promise<void> | null = null

  async function ensureLoaded(): Promise<void> {
    if (loaded.value) return
    if (!inflight) {
      inflight = api.getOrgModules()
        .then(res => { modules.value = res.modules; loaded.value = true })
        .catch(e => { console.warn('[org-modules] load failed; treating all modules as on', e) })
        .finally(() => { inflight = null })
    }
    await inflight
  }

  /** Is the module switched on for the org? (ignores role) */
  function isEnabled(key: OrgModuleKey): boolean {
    return modules.value[key] !== false
  }

  /** Can the current (effective) user see the module? HR always can. */
  function isVisible(key: OrgModuleKey): boolean {
    const auth = useAuthStore()
    // HR sees switched-off modules everywhere (they can always reach them).
    if (auth.isHrUser) return true
    return isEnabled(key)
  }

  async function setEnabled(key: OrgModuleKey, enabled: boolean): Promise<void> {
    const prev = modules.value[key]
    modules.value = { ...modules.value, [key]: enabled }   // optimistic
    try {
      const res = await api.updateOrgModules({ [key]: enabled })
      modules.value = res.modules
    } catch (e) {
      modules.value = { ...modules.value, [key]: prev }
      throw e
    }
  }

  const enabledCount = computed(() => Object.values(modules.value).filter(Boolean).length)

  return { modules, loaded, ensureLoaded, isEnabled, isVisible, setEnabled, enabledCount }
})
